import * as React from 'react';

import { ChevronLeftIcon, ChevronRightIcon, MoreHorizontalIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@crm/shared/lib/styles/utils';

import { Button } from '../Button';

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => {
    const { t } = useTranslation();

    return (
        <nav
            role="navigation"
            aria-label={t('crm.common.pagination.navigationAriaLabel')}
            data-slot="pagination"
            className={cn('mx-auto flex w-full justify-center', className)}
            {...props}
        />
    );
};

const PaginationContent = ({ className, ...props }: React.ComponentProps<'ul'>) => (
    <ul
        data-slot="pagination-content"
        className={cn('flex items-center gap-0.5', className)}
        {...props}
    />
);

const PaginationItem = (props: React.ComponentProps<'li'>) => (
    <li data-slot="pagination-item" {...props} />
);

type PaginationLinkProps = {
    isActive?: boolean;
} & Pick<React.ComponentProps<typeof Button>, 'size'> &
    React.ComponentProps<'a'>;

const PaginationLink = ({
    children,
    className,
    isActive,
    size = 'icon',
    ...props
}: PaginationLinkProps) => (
    <Button
        variant={isActive ? 'outline' : 'ghost'}
        size={size}
        className={cn(className)}
        nativeButton={false}
        render={
            <a
                aria-current={isActive ? 'page' : undefined}
                data-slot="pagination-link"
                data-active={isActive}
                {...props}
            >
                {children}
            </a>
        }
    />
);

const PaginationPrevious = ({
    className,
    text,
    ...props
}: React.ComponentProps<typeof PaginationLink> & { text?: string }) => {
    const { t } = useTranslation();

    return (
        <PaginationLink
            aria-label={t('crm.common.pagination.previousAriaLabel')}
            size="default"
            className={cn('pl-1.5!', className)}
            {...props}
        >
            <ChevronLeftIcon data-icon="inline-start" />
            <span className="hidden sm:block">{text ?? t('crm.common.pagination.previous')}</span>
        </PaginationLink>
    );
};

const PaginationNext = ({
    className,
    text,
    ...props
}: React.ComponentProps<typeof PaginationLink> & { text?: string }) => {
    const { t } = useTranslation();

    return (
        <PaginationLink
            aria-label={t('crm.common.pagination.nextAriaLabel')}
            size="default"
            className={cn('pr-1.5!', className)}
            {...props}
        >
            <span className="hidden sm:block">{text ?? t('crm.common.pagination.next')}</span>
            <ChevronRightIcon data-icon="inline-end" />
        </PaginationLink>
    );
};

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => {
    const { t } = useTranslation();

    return (
        <span
            data-slot="pagination-ellipsis"
            className={cn(
                "flex size-8 items-center justify-center [&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}
        >
            <MoreHorizontalIcon aria-hidden />
            <span className="sr-only">{t('crm.common.pagination.morePages')}</span>
        </span>
    );
};

export {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
};
